/**
 * A month of a schedule, as one keyed answer.
 *
 * **A schedule is not a row window**, and this is the file that says why. The
 * window is a range of row *indexes* over one ordered list with one total count;
 * a schedule has neither. It has days -- each with its own page and its own
 * count -- and no global ordering at all, so forcing it through the window would
 * mean inventing an index space that does not exist and then maintaining it.
 *
 * What it is instead is a bounded, cacheable answer to a question with an
 * obvious key: this view, this month, this timezone, this search. That is
 * TanStack Query, which is what the plan says everything the server owns should
 * be -- the row buffer being the one exception, for reasons that do not apply
 * here. Moving to another month is a different key, so the month just left is
 * still cached when somebody moves back.
 */
import { queryOptions, useQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { RowData } from '@/api/types'
import type { RowSearch } from '../rows/rowQuery'

/** The rows placed on one day, and how many there are in total. */
export interface ScheduleDay {
  count: number
  results: RowData[]
}

/** A month's answer: one entry per day that has rows, keyed `YYYY-MM-DD`. */
export type ScheduleDays = Record<string, ScheduleDay>

interface ScheduleResponse {
  rows?: Record<string, { count?: number | null; results?: unknown[] } | undefined>
}

/**
 * How many rows a day cell asks for.
 *
 * More than it can draw, so that "+4 more" is a number rather than "more" --
 * the count is the whole day's, so the difference is known without asking again.
 */
export const ROWS_PER_DAY = 10

export interface ScheduleWindow {
  from: string
  to: string
  timezone: string
}

export function scheduleRowsQuery(
  tableId: number,
  viewId: number,
  window: ScheduleWindow,
  search: RowSearch,
) {
  return queryOptions({
    // Keyed under the *table* rather than the view, so that a row created,
    // changed or deleted anywhere in the table can invalidate every month of
    // every schedule over it with one prefix -- which is what the realtime row
    // handlers do, having no window to patch here.
    queryKey: qk.scheduleRows(tableId, viewId, {
      ...window,
      search: search.term,
      mode: search.mode,
    }),
    // No abort signal, which is what every other query here does and is not an
    // oversight: openapi-fetch would build a `Request` around it, and under
    // jsdom that throws -- jsdom supplies an `AbortController` while `Request`
    // comes from Node, which refuses a signal that is not its own. The row
    // window's fetcher carries one because it must, and pays for it with a test
    // seam and a spec that runs outside jsdom. A month is one small request that
    // TanStack Query already de-duplicates; there is nothing here worth that.
    queryFn: async () => {
      const body = await unwrap<ScheduleResponse>(
        getApi().GET('/api/database/views/schedule/{view_id}/', {
          params: {
            path: { view_id: viewId },
            query: {
              from_timestamp: window.from,
              to_timestamp: window.to,
              user_timezone: window.timezone,
              limit: ROWS_PER_DAY,
              offset: 0,
              include: 'row_metadata',
              ...(search.term === ''
                ? {}
                : { search: search.term, search_mode: search.mode }),
            },
          },
        }),
      )

      const days: ScheduleDays = {}
      for (const [key, day] of Object.entries(body.rows ?? {})) {
        if (day === undefined) continue
        days[key] = {
          // A day the server mentions has a count; one it does not mention has
          // no rows, and is simply absent from the answer.
          count: day.count ?? 0,
          results: (day.results ?? []) as RowData[],
        }
      }
      return days
    },
  })
}

export function useScheduleRows(
  tableId: number,
  viewId: number,
  window: ScheduleWindow,
  search: RowSearch,
) {
  return useQuery(scheduleRowsQuery(tableId, viewId, window, search))
}
